import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchQaHistory } from './api';
import {
  backendVersionLabel,
  formatCount,
  formatDuration,
  formatTimestamp,
  formatUsd,
  runCostLabel,
  runStatusClasses,
} from './format';
import { RunBreakdown } from './RunBreakdown';
import type { QaHistoryGroup, QaRun } from './types';

/**
 * Where the money went, and whether the total can still move.
 *
 * A dummy run spends nothing by design, so `$0` is the expected reading rather
 * than a gap. A total the run has not settled yet is marked with `+` in the cell;
 * this explains it.
 */
function costTitle(run: QaRun): string {
  const cost = run.cost || {};
  if (cost.total == null) {
    return 'Spend has not been read for this run yet. Open it to load the figure.';
  }
  const perApp = Object.entries(cost.per_app || {})
    .map(([app, amount]) => `${app}: ${formatUsd(amount as number)}`)
    .join('\n');
  const caveat = cost.final
    ? 'Final: every scan and call has finished.'
    : 'Spend so far — work submitted by this run is still generating.';
  return perApp ? `${perApp}\n\n${caveat}` : caveat;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
      aria-hidden="true"
    >
      <path d="M7.5 5.5 12.5 10l-5 4.5V5.5Z" />
    </svg>
  );
}

/**
 * Tests history: every run a test has ever had, grouped under the test's name so
 * several runs of the same test sit adjacent and can be compared directly. The
 * leading columns are the versions that make a comparison meaningful — the
 * backend build measured, then the test definition used.
 *
 * Grouping is done server-side; expanding a run loads its breakdown on demand.
 */
export function TestsHistoryPage() {
  const [params, setParams] = useSearchParams();
  const focusRun = params.get('run');

  const [groups, setGroups] = useState<QaHistoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [openRun, setOpenRun] = useState<string | null>(focusRun);

  useEffect(() => {
    let cancelled = false;
    fetchQaHistory()
      .then((data) => {
        if (cancelled) return;
        setGroups(data.groups);
        // Land with a group already open: the newest one, or the one holding the
        // run the Run test page linked to.
        const target = focusRun
          ? data.groups.find((group) => group.runs.some((run) => run.id === focusRun))
          : data.groups[0];
        if (target) setOpenGroups({ [target.name]: true });
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Mount-only: `focusRun` seeds the initial expansion and should not reopen
    // groups the operator has since collapsed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalRuns = useMemo(
    () => groups.reduce((sum, group) => sum + group.run_count, 0),
    [groups],
  );

  const toggleRun = (runId: string) => {
    const next = openRun === runId ? null : runId;
    setOpenRun(next);
    if (next) setParams({ run: next }, { replace: true });
    else setParams({}, { replace: true });
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading history…</div>;
  }

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Tests history</h1>
        <p className="text-sm text-slate-500 mt-1">
          {totalRuns.toLocaleString()} run{totalRuns === 1 ? '' : 's'} across{' '}
          {groups.length.toLocaleString()} test{groups.length === 1 ? '' : 's'}. Expand a run for its
          per-application breakdown, slowest scans and failures.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft px-5 py-8 text-center text-sm text-slate-500">
          No runs yet.
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const open = Boolean(openGroups[group.name]);
            return (
              <div
                key={group.name}
                className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden"
              >
                <button
                  onClick={() =>
                    setOpenGroups((current) => ({ ...current, [group.name]: !current[group.name] }))
                  }
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
                >
                  <Chevron open={open} />
                  <span className="flex-1">
                    <span className="text-base font-semibold text-slate-900">{group.name}</span>
                    <span className="block text-xs text-slate-500">
                      {group.run_count} run{group.run_count === 1 ? '' : 's'} ·{' '}
                      {group.version_count} version{group.version_count === 1 ? '' : 's'} · last ran{' '}
                      {formatTimestamp(group.latest_run_at)}
                    </span>
                  </span>
                </button>

                {open && (
                  <table className="w-full text-sm border-t border-slate-100">
                    <thead>
                      <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
                        {/* Backend first, then the test's own version: two runs of
                            the same test version mean different things if they
                            measured different builds. */}
                        <th className="px-5 py-2.5">Backend</th>
                        <th className="px-5 py-2.5">Version</th>
                        <th className="px-5 py-2.5">Run</th>
                        <th className="px-5 py-2.5">Status</th>
                        <th className="px-5 py-2.5">Applications</th>
                        <th className="px-5 py-2.5">Submitted</th>
                        <th className="px-5 py-2.5">Total cost</th>
                        <th className="px-5 py-2.5" title="Start to the last scan finishing">
                          Total test time
                        </th>
                        <th className="px-5 py-2.5">Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.runs.map((run) => {
                        const expanded = openRun === run.id;
                        const apps = Object.keys(run.targets || {});
                        return [
                          <tr
                            key={run.id}
                            onClick={() => toggleRun(run.id)}
                            className={`border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${
                              expanded ? 'bg-slate-50' : ''
                            }`}
                          >
                            <td
                              className="px-5 py-3 font-mono text-xs text-slate-700"
                              title={
                                Object.entries(run.backend_versions || {})
                                  .map(([app, version]) => `${app}: ${version}`)
                                  .join('\n') ||
                                'No backend version was recorded for this run'
                              }
                            >
                              {backendVersionLabel(run.backend_versions)}
                            </td>
                            <td className="px-5 py-3 font-mono text-xs text-slate-500">
                              v{run.definition_version}
                            </td>
                            <td className="px-5 py-3 text-slate-600">#{run.run_number}</td>
                            <td className="px-5 py-3">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${runStatusClasses(run.status)}`}
                              >
                                {run.status}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-slate-600">{apps.length || '—'}</td>
                            <td className="px-5 py-3 text-slate-600">
                              {formatCount(run.calls_submitted)}
                              <span className="text-slate-400">
                                {' '}
                                / {formatCount(run.calls_planned)}
                              </span>
                            </td>
                            <td
                              className="px-5 py-3 text-slate-600 tabular-nums"
                              title={costTitle(run)}
                            >
                              {runCostLabel(run.cost)}
                            </td>
                            <td className="px-5 py-3 text-slate-600">
                              {formatDuration(run.started_at, run.finished_at)}
                            </td>
                            <td className="px-5 py-3 text-slate-500">
                              {formatTimestamp(run.started_at || run.created_at)}
                            </td>
                          </tr>,
                          expanded ? (
                            <tr key={`${run.id}-detail`} className="border-b border-slate-100">
                              <td colSpan={9} className="px-5 py-4 bg-slate-50/60">
                                {run.error && (
                                  <div className="mb-3 rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm">
                                    {run.error}
                                  </div>
                                )}
                                <RunBreakdown runId={run.id} />
                              </td>
                            </tr>
                          ) : null,
                        ];
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
